/**
 * RFQ Modal - Variant formatting and form validation
 */

// Hide unwanted labels before DOM is fully painted
(() => {
  const hideLabels = () => {
    const pattern =
      /^-?\s*(Quote Information|Product Description|Additional Info|Project Name|Product Needed By).*:?\s*$/i;
    document.querySelectorAll("[class*='omgrfq'] p").forEach((p) => {
      if (pattern.test(p.textContent.trim())) p.style.display = "none";
    });
  };

  if (document.readyState !== "loading") hideLabels();
  else document.addEventListener("DOMContentLoaded", hideLabels);
})();

// Variant Formatter
const productCache = {};
const pendingFetches = {};
let domCache = new WeakMap();
let debounceTimer = null;
let isProcessing = false;
let pendingRun = false;

const FORM_SELECTOR = '.omgrfq-form, .omgrfq-form-wrapper form, [class*="omgrfq"] form';
const RFQ_HASH_VIEW_QUOTE = '#rfq-view-quote';
const RFQ_HASH_ADD_QUOTE = '#rfq-add-quote';

function parseNodes(htmlStr) {
  const doc = new DOMParser().parseFromString(htmlStr, 'text/html');
  return Array.from(doc.body.childNodes).map((n) => n.cloneNode(true));
}

const HIDDEN_PHASE1_LABELS = [
  "Quote Information",
  "Product Description",
  "Additional Info",
  "Project Name",
  "Product Needed By",
];

const HIDDEN_LABELS_PATTERN = new RegExp(
  `^-?\\s*(${HIDDEN_PHASE1_LABELS.map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, ".*")).join("|")}).*:?\\s*$`,
  "i",
);

const VARIANT_SELECTOR = ".omgrfq_option:not([data-formatted='true'])";

async function getProductInfo(productHandle) {
  if (productCache[productHandle]) return productCache[productHandle];
  if (pendingFetches[productHandle]) return pendingFetches[productHandle];

  pendingFetches[productHandle] = fetch(`/products/${productHandle}.js`)
    .then((response) => {
      if (!response.ok) throw new Error("Product not found");
      return response.json();
    })
    .then((data) => {
      productCache[productHandle] = data;
      delete pendingFetches[productHandle];
      return data;
    })
    .catch(() => {
      delete pendingFetches[productHandle];
      return null;
    });

  return pendingFetches[productHandle];
}

function extractProductHandle(element) {
  if (domCache.has(element)) return domCache.get(element);

  const productRow = element.closest(
    "tr, .omgrfq_product_row, .omgrfq_product_item, .omgrfq_product_image",
  );
  if (!productRow) return null;

  const productLink =
    productRow.querySelector(
      ".omgrfq_product_title a, a[href*='/products/']",
    ) ||
    productRow.parentElement?.querySelector(
      ".omgrfq_product_title a, a[href*='/products/']",
    );

  const handle =
    productLink?.getAttribute("href")?.match(/\/products\/([^/?]+)/)?.[1] ||
    null;
  domCache.set(element, handle);
  return handle;
}

function hidePhase1Labels() {
  const paragraphs = document.querySelectorAll("[class*='omgrfq'] p");
  paragraphs.forEach((p) => {
    if (HIDDEN_LABELS_PATTERN.test(p.textContent.trim())) {
      p.style.display = "none";
    }
  });
}

function extractOriginalVariantText(element) {
  const variantValues = element.querySelectorAll(".variant-value");
  if (variantValues.length > 0) {
    return Array.from(variantValues, (v) => v.textContent.trim()).join(" / ");
  }
  return element.textContent.trim().replace(/(\w+:)\s*\1+/gi, "$1");
}

function isProperlyFormatted(element) {
  if (element.dataset.formatted !== "true") return false;
  if (!element.querySelector(".variant-item .variant-value")) return false;
  for (const label of element.querySelectorAll(".variant-label")) {
    if (/(\w+:)\s*\1/i.test(label.textContent.trim())) return false;
  }
  return true;
}

function cleanPreviousFormat(element) {
  const originalText = extractOriginalVariantText(element);
  element.replaceChildren();
  element.removeAttribute("data-formatted");
  return originalText;
}

async function formatVariants() {
  if (isProcessing) { pendingRun = true; return; }
  isProcessing = true;

  try {
    const elements = document.querySelectorAll(VARIANT_SELECTOR);
    if (elements.length === 0) return;

    const handles = new Set();
    elements.forEach((el) => {
      const handle = extractProductHandle(el);
      if (handle && !productCache[handle]) handles.add(handle);
    });

    const handlesArray = [...handles];
    const batchSize = 5;
    for (let i = 0; i < handlesArray.length; i += batchSize) {
      const batch = handlesArray.slice(i, i + batchSize);
      await Promise.all(batch.map((h) => getProductInfo(h)));
    }

    const processElement = (element) => {
      if (element.dataset.formatted === "true") return;
      const originalText = cleanPreviousFormat(element);
      if (!originalText) return;

      const productHandle = extractProductHandle(element);
      if (!productHandle) {
        element.textContent = originalText;
        return;
      }

      const productData = productCache[productHandle];
      if (!productData?.options) {
        element.textContent = originalText;
        return;
      }

      const optionNames = productData.options.map((opt) => opt.name);
      const variants = originalText.split(" / ");

      const html = variants.reduce((acc, variant, i) => {
        const value = variant.trim();
        if (!value) return acc;
        const cleanValue = optionNames[i]
          ? value
              .replace(new RegExp(`^${optionNames[i]}:?\\s*`, "i"), "")
              .trim() || value
          : value;
        const label = optionNames[i]
          ? `<span class="variant-label">${optionNames[i]}:</span> `
          : "";
        return (
          acc +
          `<span class="variant-item">${label}<span class="variant-value">${cleanValue}</span></span>`
        );
      }, "");

      element.replaceChildren(...parseNodes(html));
      element.dataset.formatted = "true";
    };

    const chunkSize = 10;
    for (let i = 0; i < elements.length; i += chunkSize) {
      const chunk = Array.from(elements).slice(i, i + chunkSize);
      chunk.forEach(processElement);
      if (i + chunkSize < elements.length) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
      }
    }
  } finally {
    isProcessing = false;
    if (pendingRun) {
      pendingRun = false;
      setTimeout(() => formatVariants(), 0);
    }
  }
}

function processRFQContent() {
  hidePhase1Labels();
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(async () => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => formatVariants(), { timeout: 300 });
    } else {
      await formatVariants();
    }
  }, 150);
}

function forceReprocess() {
  const elements = document.querySelectorAll(".omgrfq_option[data-formatted]");
  elements.forEach((el) => el.removeAttribute("data-formatted"));
  domCache = new WeakMap();
  processRFQContent();
}

// Form Validation
const ERROR_CLASS = "input-error";
const ERROR_MESSAGE_CLASS = "input-error-message";
const ERROR_TEXT = "Required field";
const INPUT_SELECTOR =
  "input:not([type=checkbox]):not([type=hidden]):not([type=submit]):not([type=button]), select, textarea";

function clearError(input) {
  input.classList.remove(ERROR_CLASS);
  input.parentElement?.querySelector(`.${ERROR_MESSAGE_CLASS}`)?.remove();
}

function showError(input) {
  if (input.classList.contains(ERROR_CLASS)) return;
  input.classList.add(ERROR_CLASS);
  if (!input.parentElement?.querySelector(`.${ERROR_MESSAGE_CLASS}`)) {
    const errorSpan = document.createElement("span");
    errorSpan.className = ERROR_MESSAGE_CLASS;
    errorSpan.textContent = ERROR_TEXT;
    input.insertAdjacentElement("afterend", errorSpan);
  }
}

const isInputRequired = (input) =>
  input.hasAttribute("required") || input.classList.contains("required");

function isInputVisible(input) {
  if (input.type === "hidden") return false;
  if (!input.offsetParent) return false;
  const style = window.getComputedStyle(input);
  return style.display !== "none" && style.visibility !== "hidden";
}

function validateInput(input) {
  if (!isInputVisible(input) || !isInputRequired(input)) {
    clearError(input);
    return true;
  }
  const value =
    input.tagName === "SELECT"
      ? input.value || input.options?.[input.selectedIndex]?.value || ""
      : input.value || "";
  if (value.trim() === "") {
    showError(input);
    return false;
  }
  clearError(input);
  return true;
}

const getFormInputs = (form) => form.querySelectorAll(INPUT_SELECTOR);

function validateForm(form) {
  const inputs = getFormInputs(form);
  let isFormValid = true,
    firstInvalid = null;

  for (let i = 0; i < inputs.length; i++) {
    if (!validateInput(inputs[i])) {
      if (!firstInvalid) firstInvalid = inputs[i];
      isFormValid = false;
    }
  }

  if (firstInvalid) {
    firstInvalid.focus();
    firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  return isFormValid;
}

function setupInputListeners(input) {
  if (input.dataset.errorListenerInit) return;
  input.dataset.errorListenerInit = "true";
  input.addEventListener("input", () => clearError(input));
  input.addEventListener("change", () => clearError(input));
  input.addEventListener("blur", function () {
    if (isInputRequired(this) && this.value?.trim() === "") showError(this);
  });
}

function initFormValidation(form) {
  if (!form) return;
  form.setAttribute("novalidate", "true");
  if (!form.dataset.validationInit) {
    form.dataset.validationInit = "true";
    form.addEventListener(
      "submit",
      function (e) {
        if (!validateForm(form)) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      },
      true,
    );
  }
  getFormInputs(form).forEach(setupInputListeners);
}

function initAllForms() {
  document.querySelectorAll(FORM_SELECTOR).forEach(initFormValidation);
}

function refreshAllForms() {
  document.querySelectorAll(FORM_SELECTOR).forEach((form) => {
    getFormInputs(form).forEach(setupInputListeners);
  });
}

// DOM Mutation Observer
let mutationDebounce = null;
let observer = null;

function observeChanges() {
  if (observer) return;

  observer = new MutationObserver((mutations) => {
    let shouldProcess = false,
      productRemoved = false,
      formFieldsAdded = false,
      labelsAdded = false;

    for (let i = 0; i < mutations.length; i++) {
      const mutation = mutations[i];

      if (mutation.addedNodes.length > 0) {
        for (let j = 0; j < mutation.addedNodes.length; j++) {
          const node = mutation.addedNodes[j];
          if (node.nodeType !== 1) continue;

          if (
            node.matches?.("[class*='omgrfq'] p") ||
            node.querySelector?.("[class*='omgrfq'] p")
          ) {
            labelsAdded = true;
          }

          if (
            node.matches?.(".omgrfq_option, [class*='omgrfq']") ||
            node.querySelector?.(".omgrfq_option")
          ) {
            shouldProcess = true;
          }
          if (node.matches?.(FORM_SELECTOR)) initFormValidation(node);
          else
            node.querySelectorAll?.(FORM_SELECTOR)?.forEach(initFormValidation);
          if (
            node.matches?.("input, select, textarea") ||
            node.querySelector?.("input, select, textarea")
          )
            formFieldsAdded = true;
        }
      }

      if (mutation.removedNodes.length > 0) {
        for (let j = 0; j < mutation.removedNodes.length; j++) {
          const node = mutation.removedNodes[j];
          if (
            node.nodeType === 1 &&
            (node.classList?.contains("omgrfq_product_row") ||
              node.classList?.contains("omgrfq_product_item") ||
              node.querySelector?.(".omgrfq_product_row, .omgrfq_product_item"))
          ) {
            productRemoved = true;
            break;
          }
        }
        if (productRemoved) break;
      }
    }

    if (labelsAdded) {
      hidePhase1Labels();
    }

    clearTimeout(mutationDebounce);
    mutationDebounce = setTimeout(() => {
      if (productRemoved) forceReprocess();
      else if (shouldProcess) processRFQContent();
      if (formFieldsAdded) refreshAllForms();
    }, 100);
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

// Initialization
const init = () => {
  const runInit = () => {
    processRFQContent();
    initAllForms();
    observeChanges();
  };

  if ("requestIdleCallback" in window) {
    requestIdleCallback(runInit, { timeout: 500 });
  } else {
    setTimeout(runInit, 100);
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

// Click event delegation
let clickDebounce = null;
document.addEventListener(
  "click",
  (e) => {
    const target = e.target;

    if (
      !target.closest(
        '[data-omgrfq], .omgrfq-btn, .rfq-button, [class*="rfq"], .omgrfq_remove_product, [class*="remove"], [class*="delete"]',
      )
    ) {
      return;
    }

    clearTimeout(clickDebounce);

    if (
      target.closest('[data-omgrfq], .omgrfq-btn, .rfq-button, [class*="rfq"]')
    ) {
      clickDebounce = setTimeout(() => {
        processRFQContent();
        initAllForms();
        refreshAllForms();
      }, 300);
    } else if (
      target.closest(
        '.omgrfq_remove_product, [class*="remove"], [class*="delete"]',
      )
    ) {
      clickDebounce = setTimeout(forceReprocess, 300);
    }
  },
  { passive: true },
);

window.RFQModal = {
  formatVariants,
  forceReprocess,
  validateForm,
  validateInput,
  showError,
  clearError,
  initFormValidation,
  refreshAllForms,
};
export {
  formatVariants,
  forceReprocess,
  validateForm,
  validateInput,
  refreshAllForms,
};

// RFQ View Quote Button Trigger
(function () {
  function initRfqViewQuoteButtons() {
    document.querySelectorAll('.rfq-view-quote-trigger').forEach((button) => {
      if (button.dataset.rfqAttached) return;
      
      button.dataset.rfqAttached = 'true';
      button.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        const rfqBtn = document.querySelector('.omgrfq_view_quote_btn') ||
                       document.querySelector('.view-quote-btn') ||
                       document.querySelector('.icon-quote-cart') ||
                       document.querySelector('[class*="omgrfq"]');
        
        if (rfqBtn) rfqBtn.click();
        return false;
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRfqViewQuoteButtons);
  } else {
    initRfqViewQuoteButtons();
  }

  document.addEventListener('shopify:section:load', initRfqViewQuoteButtons);
  setTimeout(initRfqViewQuoteButtons, 2000);
})();

// RFQ Continue Shopping Button Redirect
(function () {
  function interceptContinueShoppingButton() {
    const continueBtn = document.querySelector('.omgrfq_continue_shopping-button');
    
    if (!continueBtn || continueBtn.dataset.intercepted) return false;
    
    continueBtn.dataset.intercepted = 'true';
    continueBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const target = continueBtn.getAttribute('href') || continueBtn.dataset.url || '/collections';
      window.location.href = target;
      return false;
    }, { capture: true, passive: false });
    
    return true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(interceptContinueShoppingButton, 1000));
  } else {
    setTimeout(interceptContinueShoppingButton, 1000);
  }
  
  // Observer for when button is added to DOM
  const buttonObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1) {
            if ((node.classList && node.classList.contains('omgrfq_continue_shopping-button')) ||
                (node.querySelector && node.querySelector('.omgrfq_continue_shopping-button'))) {
              setTimeout(interceptContinueShoppingButton, 50);
              break;
            }
          }
        }
      }
    }
  });
  
  buttonObserver.observe(document.body, { childList: true, subtree: true });

  // Observer for when modal becomes visible - re-intercept button
  const modalObserver = new MutationObserver(() => {
    const modal = document.querySelector('.omgrfq-popup, .omgrfq-popup-snap');
    if (!modal) return;
    
    const modalStyle = window.getComputedStyle(modal);
    if (modalStyle.display !== 'none' && modalStyle.visibility !== 'hidden') {
      setTimeout(interceptContinueShoppingButton, 100);
    }
  });
  
  // Start observing for modal visibility changes
  setTimeout(() => {
    const modalWrapper = document.querySelector('.omgrfq-popup-wrapper') || document.body;
    modalObserver.observe(modalWrapper, { 
      attributes: true, 
      childList: true,
      subtree: true,
      attributeFilter: ['style', 'class']
    });
  }, 1000);
})();

// RFQ Modal Universal Hash Trigger
// #rfq-view-quote - Opens the quote cart (View Quotes)
// #rfq-add-quote - Adds product to quote (for bulk pricing)
(function () {
  let isAttached = false;

  function initRfqHashTrigger() {
    if (isAttached) return;
    isAttached = true;
    
    // Use event delegation on document to catch all RFQ hash links
    document.addEventListener('click', function (e) {
      const link = e.target.closest('a');
      if (!link) return;
      
      // Check for View Quote hash (opens quote cart)
      if (link.hash === RFQ_HASH_VIEW_QUOTE || link.getAttribute('href') === RFQ_HASH_VIEW_QUOTE) {
        e.preventDefault();
        e.stopPropagation();
        
        // Find and click the "View Quote" button
        const rfqBtn = document.querySelector('.omgrfq_view_quote_btn') ||
                       document.querySelector('.view-quote-btn') ||
                       document.querySelector('.icon-quote-cart') ||
                       document.querySelector('[class*="omgrfq"]');
        
        if (rfqBtn) {
          rfqBtn.click();
        }
        
        return false;
      }
      
      // Check for Add to Quote hash (adds product to quote)
      if (link.hash === RFQ_HASH_ADD_QUOTE || link.getAttribute('href') === RFQ_HASH_ADD_QUOTE) {
        e.preventDefault();
        e.stopPropagation();
        
        // Find and click the "Add to Quote" button (specific for bulk)
        const rfqBtn = document.querySelector('.omgrfq-btn-quotes') ||
                       document.querySelector('.omgrfq-btn') ||
                       document.querySelector('button.omgrfq-btn');
        
        if (rfqBtn) {
          rfqBtn.click();
        }
        
        return false;
      }
    }, { passive: false, capture: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRfqHashTrigger);
  } else {
    initRfqHashTrigger();
  }
})();
// Add Close Button to Empty State Modal
(function () {
  function addCloseButtonToEmptyState() {
    const emptyModal = document.querySelector('.omgrfq-popup, .omgrfq-popup-snap');
    if (!emptyModal || emptyModal.querySelector('.rfq-custom-close-btn')) return false;
    
    const hasEmptyState = emptyModal.textContent.includes('Empty List') || 
                         emptyModal.textContent.includes('Your quote is currently empty');
    
    if (!hasEmptyState) return false;
    
    const closeButton = document.createElement('button');
    closeButton.className = 'rfq-custom-close-btn';
    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    const path = document.createElementNS(svgNS, 'path');
    path.setAttribute('d', 'M18 6L6 18M6 6L18 18');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('stroke-linecap', 'round');
    svg.appendChild(path);
    closeButton.appendChild(svg);
    closeButton.setAttribute('aria-label', 'Close');
    
    closeButton.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      const overlay = document.querySelector('.omgrfq-bg');
      if (overlay) {
        overlay.click();
      } else {
        emptyModal.style.display = 'none';
        const wrapper = document.querySelector('.omgrfq-popup-wrapper');
        if (wrapper) wrapper.style.display = 'none';
      }
    });
    
    emptyModal.insertBefore(closeButton, emptyModal.firstChild);
    return true;
  }

  const observer = new MutationObserver(addCloseButtonToEmptyState);
  observer.observe(document.body, { childList: true, subtree: true });
  
  setTimeout(addCloseButtonToEmptyState, 1000);
  setTimeout(addCloseButtonToEmptyState, 3000);
})();
